import { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import ModuleLayout from '../components/ModuleLayout';
import VendorLedger from '../components/accounting/VendorLedger';
import ExportToolbar from '../components/accounting/ExportToolbar';

export default function VendorPage() {
  const { token, activeBranch } = useAuth();
  const { t } = useTranslation();
  const { lang } = useLanguage();
  const branchId = activeBranch?.id;
  const [vendorData, setVendorData] = useState(null);
  const fmt = (v) => Number(v || 0).toLocaleString(lang === 'ar' ? 'ar-SA' : 'en-SA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const buildReport = useCallback(() => {
    if (!vendorData?.vendors?.length) return null;
    const sar = t('common.sar');
    return {
      kpis: [
        { label: t('vendor.totalVendors'), value: vendorData.summary.total_vendors },
        { label: t('vendor.totalPaid'), value: fmt(vendorData.summary.total_paid), unit: sar, highlight: 'positive' },
        { label: t('vendor.totalPending'), value: fmt(vendorData.summary.total_pending), unit: sar, highlight: vendorData.summary.total_pending > 0 ? 'negative' : '' },
      ],
      sections: [{
        type: 'table', title: t('accounting.vendorLedger'),
        headers: [t('common.name'), t('vendor.totalPaid'), t('vendor.totalPending'), t('common.total'), t('vendor.transactions'), t('vendor.lastPayment')],
        rows: [
          ...vendorData.vendors.map(v => ({
            cells: [
              { value: v.name },
              { value: `${fmt(v.total_paid)} ${sar}`, type: 'num', color: 'positive' },
              { value: `${fmt(v.total_pending)} ${sar}`, type: 'num', color: v.total_pending > 0 ? 'negative' : '' },
              { value: `${fmt(v.total_amount)} ${sar}`, type: 'num' },
              { value: v.transaction_count, type: 'num' },
              { value: v.last_payment_date || '-' },
            ]
          })),
          {
            _total: true,
            cells: [
              { value: t('common.total') },
              { value: `${fmt(vendorData.summary.total_paid)} ${sar}`, type: 'num', color: 'positive' },
              { value: `${fmt(vendorData.summary.total_pending)} ${sar}`, type: 'num', color: 'negative' },
              { value: `${fmt(vendorData.summary.total_paid + vendorData.summary.total_pending)} ${sar}`, type: 'num' },
              { value: '' }, { value: '' },
            ]
          }
        ]
      }]
    };
  }, [vendorData, t, lang, fmt]);

  const csvData = vendorData?.vendors?.length ? {
    data: vendorData.vendors,
    columns: [
      { key: 'name', label: t('common.name') },
      { key: 'total_paid', label: t('vendor.totalPaid') },
      { key: 'total_pending', label: t('vendor.totalPending') },
      { key: 'total_amount', label: t('common.total') },
      { key: 'transaction_count', label: t('vendor.transactions') },
      { key: 'last_payment_date', label: t('vendor.lastPayment') },
    ]
  } : null;

  return (
    <ModuleLayout moduleKey="finance">
      <div className="space-y-5" data-testid="vendor-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">{t('accounting.vendorLedger')}</h1>
            <p className="text-sm text-muted-foreground mt-1">{activeBranch?.name || ''}</p>
          </div>
          <ExportToolbar title={t('accounting.vendorLedger')} subtitle={activeBranch?.name} buildReport={buildReport} csvData={csvData} t={t} lang={lang} />
        </div>
        <VendorLedger branchId={branchId} token={token} t={t} lang={lang} onDataReady={setVendorData} />
      </div>
    </ModuleLayout>
  );
}
